const CHECKOUT_SELECTED_PROMOTION_KEY = 'RBI::CHECKOUT_SELECTED_PROMOTION';
const CHECKOUT_SELECTED_PROMOTION_TTL_MS = 30 * 60 * 1000;

type CheckoutSelectedPromotion = {
  rbiOrderId: string;
  selectedPromotionId: string;
  createdAt: number;
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
};

const readCheckoutSelectedPromotion = (): CheckoutSelectedPromotion | undefined => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    const serializedPromotion = storage.getItem(CHECKOUT_SELECTED_PROMOTION_KEY);
    if (!serializedPromotion) {
      return;
    }

    const promotion = JSON.parse(serializedPromotion) as Partial<CheckoutSelectedPromotion>;
    const isValid =
      typeof promotion.rbiOrderId === 'string' &&
      promotion.rbiOrderId.length > 0 &&
      typeof promotion.selectedPromotionId === 'string' &&
      promotion.selectedPromotionId.length > 0 &&
      typeof promotion.createdAt === 'number' &&
      promotion.createdAt >= Date.now() - CHECKOUT_SELECTED_PROMOTION_TTL_MS;

    if (!isValid) {
      storage.removeItem(CHECKOUT_SELECTED_PROMOTION_KEY);
      return;
    }

    return promotion as CheckoutSelectedPromotion;
  } catch {
    try {
      storage.removeItem(CHECKOUT_SELECTED_PROMOTION_KEY);
    } catch {
      // Storage can become unavailable in private browsing or restricted webviews.
    }
    return;
  }
};

export const clearCheckoutSelectedPromotion = (rbiOrderId?: string | null) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    if (rbiOrderId) {
      const promotion = readCheckoutSelectedPromotion();
      if (promotion && promotion.rbiOrderId !== rbiOrderId) {
        return;
      }
    }

    storage.removeItem(CHECKOUT_SELECTED_PROMOTION_KEY);
  } catch {
    // Storage can be unavailable in private browsing or restricted webviews.
  }
};

export const clearUserOwnedCheckoutSelectedPromotion = () => {
  clearCheckoutSelectedPromotion();
};

export const persistCheckoutSelectedPromotion = ({
  rbiOrderId,
  selectedPromotionId,
}: {
  rbiOrderId?: string | null;
  selectedPromotionId?: string;
}): boolean => {
  if (!rbiOrderId || !selectedPromotionId) {
    return false;
  }

  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  const promotion: CheckoutSelectedPromotion = {
    rbiOrderId,
    selectedPromotionId,
    createdAt: Date.now(),
  };

  try {
    storage.setItem(CHECKOUT_SELECTED_PROMOTION_KEY, JSON.stringify(promotion));
    return true;
  } catch {
    return false;
  }
};

export const getCheckoutSelectedPromotion = (rbiOrderId?: string | null): string | undefined => {
  if (!rbiOrderId) {
    return;
  }

  const promotion = readCheckoutSelectedPromotion();
  return promotion?.rbiOrderId === rbiOrderId ? promotion.selectedPromotionId : undefined;
};
